import {
  ChangeDetectionStrategy,
  Component,
  output,
  inject,
} from '@angular/core';

import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  CheckboxWithLabelComponent,
  FormControlComponent,
  NipValidator,
  PrimengButtonDirective,
  PrimengInputTextDirective,
  PrimengPasswordDirective,
  ReCaptchaComponent,
} from '@web/shared/ui';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { Button } from 'primeng/button';
import { generateRandomPassword } from '@web/utils';
import { SignUpAsCompanyForm } from '../../models/sign-up-as-company-form';
import { CheckboxModule } from 'primeng/checkbox';
import { NewsletterCheckboxLabelComponent } from '../../ui/newsletter-checkbox-label/newsletter-checkbox-label.component';
import { PrivacyPolicyClauseComponent } from '../../ui/privacy-policy-clause/privacy-policy-clause.component';
import { RegulationsCheckboxLabelComponent } from '../../ui/regulations-checkbox-label/regulations-checkbox-label.component';

type SignUpAsCompanyFormGroup = FormGroup<{
  companyName: FormControl<string>;
  email: FormControl<string>;
  newsletter: FormControl<boolean>;
  nip: FormControl<string>;
  password: FormControl<string>;
  recaptcha: FormControl<boolean>;
  regulations: FormControl<boolean>;
  username: FormControl<string>;
}>;

@Component({
  selector: 'app-sign-up-as-company-form',
  imports: [
    Button,
    CheckboxModule,
    FormControlComponent,
    InputTextModule,
    NewsletterCheckboxLabelComponent,
    PasswordModule,
    PrimengButtonDirective,
    PrimengInputTextDirective,
    PrimengPasswordDirective,
    PrivacyPolicyClauseComponent,
    ReactiveFormsModule,
    RegulationsCheckboxLabelComponent,
    CheckboxWithLabelComponent,
    ReCaptchaComponent,
  ],
  templateUrl: './sign-up-as-company-form.component.html',
  styleUrl: './sign-up-as-company-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignUpAsCompanyFormComponent {
  private readonly builder = inject(FormBuilder);

  public readonly submitClickEvent = output<SignUpAsCompanyForm>();

  protected readonly form = this.buildForm();

  public handleSubmitClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  public handleGeneratePasswordClick(): void {
    this.form.controls.password.patchValue(generateRandomPassword());
  }

  private toData(): SignUpAsCompanyForm {
    const {
      username,
      email,
      password,
      companyName,
      nip,
      regulations,
      newsletter,
    } = this.form.getRawValue();

    return new SignUpAsCompanyForm(
      username,
      email,
      password,
      companyName,
      nip,
      regulations,
      newsletter,
    );
  }

  private buildForm(): SignUpAsCompanyFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      recaptcha: this.builder.nonNullable.control<boolean>(false, [Validators.required, Validators.requiredTrue]),
      username: this.builder.nonNullable.control('', [Validators.required]),
      email: this.builder.nonNullable.control('', [Validators.required]),
      password: this.builder.nonNullable.control('', [Validators.required]),
      companyName: this.builder.nonNullable.control(''),
      nip: this.builder.nonNullable.control('', [Validators.required, NipValidator.nip]),
      regulations: this.builder.nonNullable.control(false, [Validators.required, Validators.requiredTrue]),
      newsletter: this.builder.nonNullable.control(false),
    });
  }
}
